'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal } from '@/app/hooks';
import type {
  ChangedImagePrompt,
  FormImagePrompt,
  FormImagePromptWithSize,
  ImageUploadFile,
  ImageUploadFileInfo,
  UploadImageType,
} from '@/app/types';
import { ensureSingleImageSize, getFirstRatioMismatchIndex, getHomeCropCancelImageIds } from '@/app/utils';
import { handleChangeImageRange } from '@/app/utils/client';

type CropContext = {
  mode: 'upload' | 'mode-switch';
  baselineImageIds: string[];
};

type UseHomeImg2VideoRatioCheckOptions = {
  enabled: boolean;
  imagePrompts: (FormImagePrompt | null)[];
  maxImageSize: number;
  userInfo?: unknown;
  setImages: (updater: FormImagePrompt[] | ((prev: FormImagePrompt[]) => FormImagePrompt[])) => void;
  handleImageChange: (index: number, file: ImageUploadFile) => UploadImageType | null;
  handleRemoveImage: (index: number) => void;
  handleSwapImages: (fromIndex: number, toIndex: number) => void;
};

const getImageIds = (imagePrompts: (FormImagePrompt | null)[]) =>
  imagePrompts.filter((prompt): prompt is FormImagePrompt => Boolean(prompt)).map((prompt) => prompt.id);

export const useHomeImg2VideoRatioCheck = ({
  enabled,
  imagePrompts,
  maxImageSize,
  userInfo,
  setImages,
  handleImageChange,
  handleRemoveImage,
  handleSwapImages,
}: UseHomeImg2VideoRatioCheckOptions) => {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const [cropImagePrompts, setCropImagePrompts] = useState<(FormImagePromptWithSize | null)[] | null>(null);
  const [cropFrameIndex, setCropFrameIndex] = useState(0);
  const cropContextRef = useRef<CropContext | null>(null);
  const uploadBatchRef = useRef<{ baselineImageIds: string[] } | null>(null);
  const checkVersionRef = useRef(0);

  const markUploadBatchStart = useCallback(() => {
    if (!uploadBatchRef.current) {
      uploadBatchRef.current = { baselineImageIds: getImageIds(imagePrompts) };
    }
  }, [imagePrompts]);

  const markModeSwitch = useCallback(() => {
    cropContextRef.current = { mode: 'mode-switch', baselineImageIds: [] };
  }, []);

  const prepareCropImagePrompts = useCallback(async (prompts: (FormImagePrompt | null)[]) => {
    return Promise.all(
      prompts.map(async (prompt) => {
        if (!prompt?.prompt) return null;
        return ensureSingleImageSize(prompt);
      }),
    );
  }, []);

  useEffect(() => {
    if (!enabled) {
      checkVersionRef.current += 1;
      setCropImagePrompts(null);
      cropContextRef.current = null;
      uploadBatchRef.current = null;
      return;
    }

    if (cropImagePrompts !== null) return;

    const doneImages = imagePrompts.filter((prompt) => prompt?.prompt && prompt.status === 'done');
    if (doneImages.length < 2 || imagePrompts.some((prompt) => prompt?.status === 'uploading')) return;

    const version = ++checkVersionRef.current;
    void (async () => {
      const completePrompts = await prepareCropImagePrompts(imagePrompts);
      if (version !== checkVersionRef.current || cropImagePrompts !== null) return;

      const mismatchIndex = getFirstRatioMismatchIndex(completePrompts);
      if (mismatchIndex === -1) {
        cropContextRef.current = null;
        uploadBatchRef.current = null;
        return;
      }

      const pendingContext =
        cropContextRef.current ??
        (uploadBatchRef.current
          ? { mode: 'upload' as const, baselineImageIds: uploadBatchRef.current.baselineImageIds }
          : { mode: 'upload' as const, baselineImageIds: getImageIds(imagePrompts).slice(0, 1) });
      cropContextRef.current = pendingContext;
      setCropImagePrompts(completePrompts);
      setCropFrameIndex(mismatchIndex);
    })();

    return () => {
      checkVersionRef.current += 1;
    };
  }, [cropImagePrompts, enabled, imagePrompts, prepareCropImagePrompts]);

  const handleBatchSaveCropImages = useCallback(
    async (changedImagePrompts: ChangedImagePrompt[]) => {
      if (changedImagePrompts.length === 0) return;

      try {
        const processedPrompts = await Promise.all(
          changedImagePrompts.map(async ({ id, range, cropRatio, imagePrompt }) => {
            const result = await handleChangeImageRange({
              range,
              imagePrompt,
              maxImageSize,
              allowTempMode: true,
              userInfo,
            });
            if (!result) return null;
            if ('error' in result && result.error) {
              toast({
                type: 'error',
                message:
                  result.error.reason === 'AuditSubmitIllegal'
                    ? t('create.createForm.upload.error.AuditSubmitIllegal')
                    : t('loadImageError'),
              });
              return null;
            }
            if (!('imagePrompt' in result)) return null;

            const nextPrompt = result.imagePrompt;
            if (cropRatio !== undefined) nextPrompt.cropRatio = cropRatio;
            return { id, prompt: nextPrompt };
          }),
        );
        const validPrompts = processedPrompts.filter((item): item is NonNullable<typeof item> => item !== null);
        if (validPrompts.length > 0) {
          setImages((prev) =>
            prev.map((imagePrompt) => {
              const changed = validPrompts.find((item) => item.id === imagePrompt.id);
              return changed ? { ...imagePrompt, status: 'done' as const, prompt: changed.prompt } : imagePrompt;
            }),
          );
        }
        cropContextRef.current = null;
        uploadBatchRef.current = null;
      } catch {
        toast({ type: 'error', title: t('create.img2VideoForm.imageError') });
      }
    },
    [maxImageSize, setImages, t, userInfo],
  );

  const handleCloseCropModal = useCallback(() => {
    setCropImagePrompts(null);
    cropContextRef.current = null;
    uploadBatchRef.current = null;
  }, []);

  const removeUnconfirmedImages = useCallback(() => {
    const context = cropContextRef.current;
    if (context) {
      const idsToRemove = getHomeCropCancelImageIds({
        mode: context.mode,
        imagePrompts,
        baselineImageIds: context.baselineImageIds,
      });
      const idsToRemoveSet = new Set(idsToRemove);
      setImages((prev) => prev.filter((prompt) => !idsToRemoveSet.has(prompt.id)));
    }
    handleCloseCropModal();
  }, [handleCloseCropModal, imagePrompts, setImages]);

  const handleCancelCrop = useCallback(() => {
    openConfirmDialog({
      title: t('create.img2VideoForm.cancelCropConfirmDialog.title'),
      content: t('create.img2VideoForm.cancelCropConfirmDialog.content'),
      secondaryButtonText: t('create.img2VideoForm.cancelCropConfirmDialog.confirmButton'),
      primaryButtonText: t('create.img2VideoForm.cancelCropConfirmDialog.cancelButton'),
      onSecondaryAction: removeUnconfirmedImages,
    });
  }, [openConfirmDialog, removeUnconfirmedImages, t]);

  const handleSingleImageChange = useCallback(
    (index: number, file: ImageUploadFile, _fileInfo?: ImageUploadFileInfo) => {
      if (file.status === 'removed') {
        handleRemoveImage(index);
        setCropImagePrompts((current) => current?.filter((_, currentIndex) => currentIndex !== index) ?? null);
        setCropFrameIndex((current) => Math.max(0, Math.min(current, imagePrompts.length - 2)));
        return null;
      }
      return handleImageChange(index, file);
    },
    [handleImageChange, handleRemoveImage, imagePrompts.length],
  );

  return {
    cropFrameIndex,
    cropImagePrompts,
    handleBatchSaveCropImages,
    handleCancelCrop,
    handleCloseCropModal,
    handleSingleImageChange,
    handleSwapImages,
    markModeSwitch,
    markUploadBatchStart,
    setCropFrameIndex,
  };
};
